import * as Units from '../../Units';
import {
  createXAxisConfig,
  createYAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {ChartTheme} from '../theme';
import {ChartConfig, ChartDataPoint, ReferenceLine} from '../types';

/** The grassland temperature sum's threshold, in °C·d (start of vegetation). */
export const GRASSLAND_THRESHOLD = 200;

/** A degree-day value as the grassland report shows it: "180 °C·d". */
export const formatDegreeDays = (value: number, units: Units.Units) =>
  `${value.toFixed(0)} ${Units.getDegreeDayUnitLabel(units)}`;

/**
 * The threshold line in the user's unit system, shared by both apps' charts:
 * its value (200 °C·d = 360 °F·d), its text, and the floor of the y axis's
 * auto upper bound, 10% above the line so it stays in view (and its label
 * above it) while the year's sum is still below it (#464). A floor, not a
 * cap: a larger sum still raises the axis.
 */
export const grasslandThresholdAxis = (
  units: Units.Units,
  threshold: number = GRASSLAND_THRESHOLD,
) => {
  const value = Units.convertDegreeDayValue(threshold, units.T) ?? threshold;
  return {
    value,
    text: formatDegreeDays(value, units),
    // x 11 / 10 rather than x 1.1: 360 * 1.1 is 396.00000000000006.
    minDomainMax: Math.ceil((value * 11) / 10),
  };
};

export interface GrasslandChartOptions {
  /** Pre-computed cumulative degree-day data with `GDD` key */
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  height?: number;
  /** The GDD threshold value, in °C·d (default 200) */
  threshold?: number;
  /** Threshold label text (shown on horizontal line), e.g. from
   * `grasslandThresholdAxis(units).text` */
  thresholdLabel?: string;
  /** Timestamp (unix seconds) when the threshold was reached */
  thresholdReachedTimestamp?: number;
  /** Formatted date label for the vertical line at the reached point */
  thresholdReachedLabel?: string;
  /** Translated series name for the tooltip */
  seriesName: string;
}

export const grasslandChart = (options: GrasslandChartOptions): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    height = 400,
    threshold = GRASSLAND_THRESHOLD,
    thresholdLabel,
    thresholdReachedTimestamp,
    thresholdReachedLabel,
    seriesName,
  } = options;

  const thresholdAxis = grasslandThresholdAxis(units, threshold);

  // Labels in the text ink: the secondary accent the lines are drawn in is
  // 3.1:1 on the webapp's light card, short of 4.5:1 for text.
  const referenceLines: ReferenceLine[] = [
    {
      type: 'horizontal',
      value: thresholdAxis.value,
      color: theme.colors.secondary,
      dashArray: '5 3',
      lineWidth: 2,
      label: thresholdLabel,
      labelPosition: 'end',
      labelColor: theme.colors.text,
    },
  ];

  if (thresholdReachedTimestamp != null) {
    referenceLines.push({
      type: 'vertical',
      value: thresholdReachedTimestamp,
      color: theme.colors.secondary,
      dashArray: '5 3',
      lineWidth: 2,
      label: thresholdReachedLabel,
      labelPosition: 'end',
      labelColor: theme.colors.text,
    });
  }

  return {
    height,
    data,
    series: [
      {
        id: 'grassland-gdd',
        type: 'area',
        dataKey: 'GDD',
        name: seriesName,
        color: theme.colors.primary,
        yAxisIndex: 0,
        interpolation: 'monotone',
        fillOpacity: 0.3,
        lineWidth: 2,
        showMarkers: false,
        zIndex: 1,
      },
    ],
    xAxis: createXAxisConfig({
      type: 'datetime',
      timezone,
      granularity: 'D',
      theme,
    }),
    yAxes: [
      {
        ...createYAxisConfig({
          id: 'gdd',
          theme,
          unitLabel: Units.getDegreeDayUnitLabel(units),
          domain: [0, 'auto'],
          precision: 0,
        }),
        minDomainMax: thresholdAxis.minDomainMax,
      },
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity: 'D',
      metric: 'T',
      units,
      theme,
      // A degree-day sum, not a temperature: "180 °C·d", not "180.0 °C".
      customFormatters: {
        'grassland-gdd': (value: number) => formatDegreeDays(value, units),
      },
    }),
    legend: createLegendConfig(false, theme),
    grid: createGridConfig(theme),
    referenceLines,
    animate: true,
  };
};
